import { useRef, useState } from "react";
import { Upload, X, ImagePlus } from "lucide-react";
import { api, formatErr } from "@/lib/api";

/**
 * Reads a File and returns a downscaled JPEG data URL (max edge maxSize px).
 */
export async function fileToDataUrl(file, maxSize = 1600, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const ratio = Math.min(1, maxSize / Math.max(img.width, img.height));
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function PhotoDrop({
  value,
  onChange,
  onPick,            // called once per uploaded file with its URL — preferred for multi-upload grids
  multiple = false,  // allow selecting / dropping multiple files at once
  aspect = "square",
  label = "Drop a photo here",
  testid = "photo-drop",
  maxSize = 1600,
}) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const uploadOne = async (file) => {
    if (!file || !file.type.startsWith("image/")) return null;
    const dataUrl = await fileToDataUrl(file, maxSize);
    const { data } = await api.post("/uploads/image", { data_url: dataUrl });
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    return data.url.startsWith("http") ? data.url : `${backend}${data.url}`;
  };

  const handleFiles = async (files) => {
    const list = Array.from(files || []).filter((f) => f && f.type.startsWith("image/"));
    if (list.length === 0) return;
    setBusy(true); setErr("");
    try {
      if (multiple && onPick) {
        // Upload sequentially — emit each URL as soon as it's ready so the parent grid fills in real time.
        for (const f of list) {
          // eslint-disable-next-line no-await-in-loop
          const url = await uploadOne(f);
          if (url) onPick(url);
        }
      } else {
        const url = await uploadOne(list[0]);
        if (url) {
          if (onPick) onPick(url);
          else if (onChange) onChange(url);
        }
      }
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  const aspectClass = aspect === "cover" ? "aspect-[16/9]" : aspect === "wide" ? "aspect-[4/3]" : "aspect-square";

  return (
    <div className="w-full">
    <div
      data-testid={testid}
      onDragOver={(e)=>{e.preventDefault(); setDragOver(true);}}
      onDragLeave={()=>setDragOver(false)}
      onDrop={(e)=>{e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files);}}
      onClick={()=>inputRef.current?.click()}
      className={`relative ${aspectClass} w-full rounded-2xl border-2 border-dashed cursor-pointer transition-all overflow-hidden
        ${dragOver ? "border-[#0A0A0A] bg-[#F2F2F0]" : "border-[#E5E5E3] hover:border-[#0A0A0A] bg-[#FAFAFA]"}`}
    >
      <input ref={inputRef} type="file" accept="image/*" multiple={multiple} className="hidden"
        onChange={(e)=>handleFiles(e.target.files)} data-testid={`${testid}-input`} />
      {value ? (
        <>
          <img src={value} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <button type="button" onClick={(e)=>{e.stopPropagation(); onChange("");}}
            className="absolute top-3 right-3 h-9 w-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center hover:bg-white shadow"
            data-testid={`${testid}-remove`}>
            <X size={16}/>
          </button>
          <div className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur text-xs">
            <ImagePlus size={12}/> Replace
          </div>
        </>
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-[#4A4A4A] text-center px-6">
          <div className="h-12 w-12 rounded-full bg-white border border-[#E5E5E3] flex items-center justify-center mb-3">
            <Upload size={18}/>
          </div>
          <p className="font-medium text-[#0A0A0A]">{busy ? "Uploading…" : label}</p>
          <p className="text-xs text-[#8A8A8A] mt-1">{busy ? "" : "Drag from your library, or click to browse"}</p>
        </div>
      )}
    </div>
    {err && <p className="text-xs text-[#B84B4B] mt-2" data-testid={`${testid}-error`}>{err}</p>}
    </div>
  );
}
